/**
 * AdvancedSearchModal — full-featured board search with archive toggle,
 * structured filters, enriched results with match context, and pagination.
 *
 * Requirements: 1.1–1.5, 2.1–2.4, 3.1–3.4, 4.1–4.8
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import * as api from '../../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MatchContext {
  readonly field: 'title' | 'description' | 'label' | 'customField' | 'cardNumber';
  readonly snippet: string;
}

interface SearchResult {
  readonly cardId: string;
  readonly cardNumber: number;
  readonly title: string;
  readonly listId: string;
  readonly listTitle: string;
  readonly swimlaneId: string;
  readonly swimlaneTitle: string;
  readonly labels: string[];
  readonly dueDate: string | null;
  readonly archived: boolean;
  readonly matchContext: MatchContext;
  readonly score: number;
}

interface SearchResponse {
  readonly data: SearchResult[];
  readonly nextCursor: string | null;
  readonly total: number;
}

export interface AdvancedSearchModalProps {
  readonly boardId: string;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly initialQuery?: string;
  readonly lists: readonly { listId: string; title: string }[];
  readonly swimlanes: readonly { swimlaneId: string; title: string }[];
  readonly labels: readonly { name: string; color: string }[];
  readonly members: readonly { userId: string; displayName: string }[];
  readonly onCardClick: (cardId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdvancedSearchModal({
  boardId,
  open,
  onClose,
  initialQuery,
  lists,
  swimlanes,
  labels,
  members,
  onCardClick,
}: AdvancedSearchModalProps): React.ReactElement {
  const [query, setQuery] = useState(initialQuery ?? '');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [selectedSwimlanes, setSelectedSwimlanes] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);
  const [dueDateStart, setDueDateStart] = useState('');
  const [dueDateEnd, setDueDateEnd] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      if (initialQuery) setQuery(initialQuery);
    }
  }, [open, initialQuery]);

  // Search function
  const executeSearch = useCallback(async (cursor?: string) => {
    if (query.trim().length < 2) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('q', query.trim());
      if (includeArchived) params.set('includeArchived', 'true');
      if (selectedLists.length > 0) params.set('listIds', selectedLists.join(','));
      if (selectedSwimlanes.length > 0) params.set('swimlaneIds', selectedSwimlanes.join(','));
      if (selectedLabels.length > 0) params.set('labels', selectedLabels.join(','));
      if (selectedAssignees.length > 0) params.set('assigneeUserIds', selectedAssignees.join(','));
      if (dueDateStart) params.set('dueDateStart', dueDateStart);
      if (dueDateEnd) params.set('dueDateEnd', dueDateEnd);
      if (cursor) params.set('cursor', cursor);

      const response = await api.get<SearchResponse>(
        `/v1/boards/${boardId}/search?${params.toString()}`,
      );

      if (cursor) {
        setResults((prev) => [...prev, ...response.data]);
      } else {
        setResults(response.data);
      }
      setNextCursor(response.nextCursor);
      setTotal(response.total);
      setHasSearched(true);
    } catch {
      // Search failed
    } finally {
      setLoading(false);
    }
  }, [query, includeArchived, selectedLists, selectedSwimlanes, selectedLabels, selectedAssignees, dueDateStart, dueDateEnd, boardId]);

  // Debounced search on query/filter change
  useEffect(() => {
    if (!open) return;
    if (query.trim().length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => executeSearch(), 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, includeArchived, selectedLists, selectedSwimlanes, selectedLabels, selectedAssignees, dueDateStart, dueDateEnd, open, executeSearch]);

  const handleResultClick = useCallback((cardId: string) => {
    onCardClick(cardId);
    onClose();
  }, [onCardClick, onClose]);

  const clearFilters = useCallback(() => {
    setSelectedLists([]);
    setSelectedSwimlanes([]);
    setSelectedLabels([]);
    setSelectedAssignees([]);
    setDueDateStart('');
    setDueDateEnd('');
    setIncludeArchived(false);
  }, []);

  const activeFilterCount = [
    selectedLists.length > 0,
    selectedSwimlanes.length > 0,
    selectedLabels.length > 0,
    selectedAssignees.length > 0,
    !!dueDateStart || !!dueDateEnd,
  ].filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Advanced Search</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 mt-2">
          {/* Query input */}
          <div>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search cards..."
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Search query"
            />
          </div>

          {/* Archive toggle + filter summary */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="rounded border-border"
              />
              Include archived cards
            </label>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-primary hover:underline"
              >
                Clear filters ({activeFilterCount})
              </button>
            )}
          </div>

          {/* Filters — collapsible */}
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-medium">
              Filters {activeFilterCount > 0 ? `(${activeFilterCount} active)` : ''}
            </summary>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {/* List filter */}
              <div>
                <label className="block text-[11px] text-muted-foreground mb-0.5">List</label>
                <select
                  multiple
                  value={selectedLists}
                  onChange={(e) => setSelectedLists(Array.from(e.target.selectedOptions, (o) => o.value))}
                  className="w-full h-20 rounded border border-border bg-background text-xs px-1"
                >
                  {lists.map((l) => (
                    <option key={l.listId} value={l.listId}>{l.title}</option>
                  ))}
                </select>
              </div>

              {/* Swimlane filter */}
              <div>
                <label className="block text-[11px] text-muted-foreground mb-0.5">Swimlane</label>
                <select
                  multiple
                  value={selectedSwimlanes}
                  onChange={(e) => setSelectedSwimlanes(Array.from(e.target.selectedOptions, (o) => o.value))}
                  className="w-full h-20 rounded border border-border bg-background text-xs px-1"
                >
                  {swimlanes.map((s) => (
                    <option key={s.swimlaneId} value={s.swimlaneId}>{s.title}</option>
                  ))}
                </select>
              </div>

              {/* Label filter */}
              <div>
                <label className="block text-[11px] text-muted-foreground mb-0.5">Label</label>
                <select
                  multiple
                  value={selectedLabels}
                  onChange={(e) => setSelectedLabels(Array.from(e.target.selectedOptions, (o) => o.value))}
                  className="w-full h-20 rounded border border-border bg-background text-xs px-1"
                >
                  {labels.map((l) => (
                    <option key={l.name} value={l.name}>{l.name}</option>
                  ))}
                </select>
              </div>

              {/* Assignee filter */}
              <div>
                <label className="block text-[11px] text-muted-foreground mb-0.5">Assignee</label>
                <select
                  multiple
                  value={selectedAssignees}
                  onChange={(e) => setSelectedAssignees(Array.from(e.target.selectedOptions, (o) => o.value))}
                  className="w-full h-20 rounded border border-border bg-background text-xs px-1"
                >
                  {members.map((m) => (
                    <option key={m.userId} value={m.userId}>{m.displayName}</option>
                  ))}
                </select>
              </div>

              {/* Due date range */}
              <div>
                <label className="block text-[11px] text-muted-foreground mb-0.5">Due after</label>
                <input
                  type="date"
                  value={dueDateStart}
                  onChange={(e) => setDueDateStart(e.target.value)}
                  className="w-full rounded border border-border bg-background text-xs px-2 py-1"
                />
              </div>
              <div>
                <label className="block text-[11px] text-muted-foreground mb-0.5">Due before</label>
                <input
                  type="date"
                  value={dueDateEnd}
                  onChange={(e) => setDueDateEnd(e.target.value)}
                  className="w-full rounded border border-border bg-background text-xs px-2 py-1"
                />
              </div>
            </div>
          </details>

          {/* Results */}
          <div>
            {hasSearched && (
              <p className="text-[11px] text-muted-foreground mb-2">
                {total} result{total !== 1 ? 's' : ''} found
              </p>
            )}

            {results.length > 0 && (
              <ul className="space-y-1" role="list" aria-label="Search results">
                {results.map((r) => (
                  <li key={r.cardId}>
                    <button
                      type="button"
                      onClick={() => handleResultClick(r.cardId)}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-mono text-muted-foreground">#{r.cardNumber}</span>
                        <span className="text-sm font-medium text-foreground truncate flex-1">{r.title}</span>
                        {r.archived && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Archived</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                        <span>{r.listTitle}</span>
                        {r.swimlaneTitle && <span>· {r.swimlaneTitle}</span>}
                        {r.matchContext.field !== 'title' && (
                          <span className="italic truncate max-w-[200px]">
                            — {r.matchContext.snippet}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {hasSearched && results.length === 0 && !loading && (
              <div className="text-center py-6 text-sm text-muted-foreground">
                <p>No results found for "{query}"</p>
                {activeFilterCount > 0 && (
                  <p className="mt-1 text-xs">Try broadening your filters or clearing them.</p>
                )}
              </div>
            )}

            {loading && (
              <p className="text-center text-xs text-muted-foreground py-4">Searching...</p>
            )}

            {nextCursor && !loading && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => executeSearch(nextCursor)}
                  className="text-xs text-primary hover:underline"
                >
                  Load more ({results.length} of {total})
                </button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
